import { useEffect, useMemo, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import Maps from '../Maps';
import PlayersTab from '../tournament/PlayersTab';
import RulesTab from '../tournament/RulesTab';
import { subscribeStages } from '../../../database/tournamentStages';
import { subscribeVotes } from '../../../database/tournamentVotes';
import {
  competitionLabel,
  finalStandings,
  focusStageId,
  hasVoteCategory,
  isSectionVisible,
  stageSummary,
  voteAverages,
} from '../../../lib/stages';
import StageStepper, { StageStatusPill } from './StageStepper';
import LeaderboardStageView from './LeaderboardStageView';
import BracketView from './BracketView';
import ChampionBanner from './ChampionBanner';
import CommunityVotePanel from './CommunityVotePanel';

const StagedTournamentView = ({ tournament, players = [], tournamentId }) => {
  const show = (key) => isSectionVisible(tournament, key);
  const stagesOn = show('stages');

  const playersById = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);

  const [stages, setStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(true);
  const [stagesError, setStagesError] = useState(null);

  useEffect(() => {
    if (!stagesOn) {
      setStagesLoading(false);
      return undefined;
    }
    setStagesLoading(true);
    setStagesError(null);
    return subscribeStages(
      tournamentId,
      (data) => {
        setStages(data);
        setStagesLoading(false);
      },
      (err) => {
        setStagesError(err);
        setStagesLoading(false);
      }
    );
  }, [tournamentId, stagesOn]);

  const [voteDocs, setVoteDocs] = useState(null);
  const hasVotes = stages.some(hasVoteCategory);

  useEffect(() => {
    if (!(stagesOn && tournament.communityAverageVisible === true && hasVotes)) {
      setVoteDocs(null);
      return undefined;
    }
    const unsubscribe = subscribeVotes(tournamentId, setVoteDocs, () => setVoteDocs(null));
    return () => {
      unsubscribe();
      setVoteDocs(null);
    };
  }, [tournamentId, stagesOn, tournament.communityAverageVisible, hasVotes]);

  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    if (!stages.length) return;
    if (!selectedId || !stages.some((s) => s.id === selectedId)) {
      setSelectedId(focusStageId(stages));
    }
  }, [stages, selectedId]);

  const tabs = [];
  if (show('stages')) {
    tabs.push({ key: 'stages', label: stagesLoading ? 'Stages' : competitionLabel(stages) });
  }
  tabs.push({ key: 'rules', label: 'Rules' });
  if (show('players')) tabs.push({ key: 'players', label: 'Players' });
  if (show('maps')) tabs.push({ key: 'maps', label: 'Maps' });

  if (tabs.length === 1) {
    return <RulesTab html={tournament.rules} />;
  }

  const index = stages.findIndex((s) => s.id === selectedId);
  const rawStage = index >= 0 ? stages[index] : null;
  // Hide leaderboard entries whose roster doc was deleted (once the roster
  // has loaded). Brackets keep their seed list intact.
  const stage =
    rawStage && rawStage.type === 'leaderboard' && players.length
      ? {
          ...rawStage,
          participants: (rawStage.participants || []).filter((id) => playersById[id]),
          ...(Array.isArray(rawStage.standings)
            ? { standings: rawStage.standings.filter((r) => playersById[r.playerId]) }
            : {}),
        }
      : rawStage;
  const prev = index > 0 ? stages[index - 1] : null;
  const next = index >= 0 ? stages[index + 1] : null;

  const lastStage = stages.length
    ? [...stages].sort((a, b) => a.order - b.order)[stages.length - 1]
    : null;
  const champion = finalStandings(stages);
  const hideChampion =
    lastStage && hasVoteCategory(lastStage) && tournament.communityAverageVisible !== true;

  const renderStagesTab = () => {
    if (stagesLoading) {
      return (
        <div className="space-y-4">
          <div className="h-24 animate-pulse rounded-md bg-muted/60" />
          <div className="h-64 animate-pulse rounded-md bg-muted/60" />
        </div>
      );
    }
    if (stagesError) {
      return (
        <div className="rounded-md border border-destructive/40 p-4 text-sm text-destructive">
          Could not load the stages.
        </div>
      );
    }
    if (stages.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
          No stages yet.
        </div>
      );
    }
    return (
      <>
        {champion?.length > 0 && !hideChampion && (
          <ChampionBanner standings={champion} playersById={playersById} />
        )}
        {stages.length > 1 && (
          <StageStepper stages={stages} selectedId={selectedId} onSelect={setSelectedId} />
        )}
        {stage && (
          <div className="space-y-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  Stage {index + 1}
                  {prev && ` · Top ${stage.advanceCount} from ${prev.name}`}
                </div>
                <h2 className="font-display text-2xl font-semibold tracking-tight">{stage.name}</h2>
                <p className="text-sm text-muted-foreground">{stageSummary(stage)}</p>
              </div>
              <StageStatusPill status={stage.status} />
            </div>

            {stage.type === 'leaderboard' ? (
              <>
                <LeaderboardStageView
                  stage={stage}
                  playersById={playersById}
                  nextStage={next}
                  emptyText={
                    prev ? `Top ${stage.advanceCount} from ${prev.name} play here.` : undefined
                  }
                  votes={voteDocs ? voteAverages(voteDocs, stage.id) : null}
                  votesHidden={
                    hasVoteCategory(stage) && tournament.communityAverageVisible !== true
                  }
                />
                {hasVoteCategory(stage) &&
                  stage.status === 'active' &&
                  tournament.votingOpen !== false && (
                    <CommunityVotePanel
                      tournamentId={tournamentId}
                      stage={stage}
                      playersById={playersById}
                      players={players}
                    />
                  )}
              </>
            ) : (
              <BracketView
                stage={stage}
                playersById={playersById}
                placeholderCount={index === 0 ? players.length : stage.advanceCount || 0}
                placeholderLabel={prev ? (n) => `${prev.name} #${n}` : undefined}
              />
            )}
          </div>
        )}
      </>
    );
  };

  const renderTabContent = (key) => {
    if (key === 'stages') return renderStagesTab();
    if (key === 'rules') return <RulesTab html={tournament.rules} />;
    if (key === 'players') return <PlayersTab players={players} />;
    if (key === 'maps') return <Maps tournamentId={tournamentId} />;
    return null;
  };

  return (
    <Tabs defaultValue={tabs[0].key} className="w-full">
      <TabsList className="w-full justify-start overflow-x-auto">
        {tabs.map((t) => (
          <TabsTrigger key={t.key} value={t.key}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((t) => (
        <TabsContent
          key={t.key}
          value={t.key}
          className={t.key === 'stages' ? 'space-y-8' : undefined}
        >
          {renderTabContent(t.key)}
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default StagedTournamentView;
